import { type FormEvent, useRef, useState } from 'react';
import { toast } from 'sonner';

import clsxm from '@/lib/clsxm';

import {
  buttonPrimary,
  CheckGlyph,
  SectionHeading,
  Stage,
  tone,
} from '@/components/agents/primitives';
import Turnstile, {
  isTurnstileEnabled,
  TURNSTILE_ERROR_MESSAGE,
  type TurnstileHandle,
} from '@/components/Turnstile';

import { WORKSHOP_CONTACT_TURNSTILE_ACTION } from '@/constants/turnstile';
import { axiosInstance } from '@/utils/httpClient';
import { WORKSHOP_CONTACT_ID } from '@/utils/scrollToSection';

const BENEFITS = [
  'A session built around your business, your goals and your team’s level',
  'Hands-on work on the real challenges your team faces',
  'Frameworks, ready-to-use templates and a plan your team can apply from day one',
];

// The values are what /api/workshop-contact has always received.
const TEAM_SIZES = [
  { value: '25k-50k', label: '5-10 people' },
  { value: '50k-100k', label: '11-20 people' },
  { value: 'over_100k', label: '20+ people' },
];

const field =
  'block w-full rounded-xl border-0 bg-white px-3.5 py-2.5 text-[15px] text-[#10101c] shadow-[0_1px_2px_rgba(15,15,40,0.04)] ring-1 ring-inset ring-[#0f0f28]/[0.12] placeholder:text-[#9a9ab0] focus:ring-2 focus:ring-inset focus:ring-primary-600 dark:bg-white/[0.04] dark:text-white dark:ring-white/[0.12] dark:placeholder:text-[#6e6e8c] dark:focus:ring-primary-400';

const label = clsxm('block text-sm font-medium', tone.ink);

export default function WorkshopsContact() {
  const [sending, setSending] = useState(false);
  const turnstileRef = useRef<TurnstileHandle>(null);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSending(true);

    const form = event.currentTarget;
    const formData = new FormData(form);

    const turnstileToken = await turnstileRef.current?.getToken();
    if (isTurnstileEnabled && !turnstileToken) {
      toast.error(TURNSTILE_ERROR_MESSAGE);
      turnstileRef.current?.reset();
      setSending(false);
      return;
    }

    try {
      const { data } = await axiosInstance.post(
        '/api/workshop-contact',
        {
          firstName: formData.get('first-name'),
          lastName: formData.get('last-name'),
          email: formData.get('email'),
          company: formData.get('company'),
          phone: formData.get('phone'),
          message: formData.get('message'),
          teamSize: formData.get('team-size'),
          turnstileToken,
        },
        { skipErrorToast: true },
      );

      if (data.message === 'Email sent successfully') {
        form.reset();
        toast.success("Thanks! We'll be in touch about your workshop shortly.");
      }
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })
        ?.response?.data?.error;
      toast.error(message || 'Failed to send message. Please try again.');
    } finally {
      // Tokens are single-use, so refresh for the next attempt either way.
      turnstileRef.current?.reset();
      setSending(false);
    }
  };

  return (
    <section
      id={WORKSHOP_CONTACT_ID}
      className='scroll-mt-8 py-6 sm:py-8'
      aria-labelledby='workshop-contact-title'
    >
      <div className='mx-auto max-w-7xl px-3 sm:px-6 lg:px-8'>
        <Stage
          variant='lavender'
          halftoneAt='100% 0%'
          halftoneReach={40}
          className='px-6 py-14 sm:px-12 sm:py-20 lg:px-16'
        >
          <div className='grid gap-12 lg:grid-cols-12 lg:gap-16'>
            <div className='lg:col-span-5'>
              <SectionHeading
                id='workshop-contact-title'
                size='md'
                title='Plan a workshop for your team.'
                tail='Built around your goals.'
                lead='Tell us a little about your team and what you’d like to achieve. We’ll get back to you to plan the session.'
              />
              <ul className='mt-8 space-y-4'>
                {BENEFITS.map((benefit) => (
                  <li
                    key={benefit}
                    className={clsxm(
                      'flex gap-3 text-[16px] leading-7',
                      tone.body,
                    )}
                  >
                    <CheckGlyph className='mt-1 h-5 w-5 flex-none' />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>

            <form
              onSubmit={onSubmit}
              className={clsxm(
                'rounded-[26px] p-6 shadow-[0_30px_60px_-36px_rgba(30,27,75,0.5)] sm:p-8 lg:col-span-7',
                tone.surface,
                tone.line,
              )}
            >
              <div className='grid gap-5 sm:grid-cols-2'>
                <div>
                  <label htmlFor='first-name' className={label}>
                    First name
                  </label>
                  <input
                    id='first-name'
                    name='first-name'
                    type='text'
                    required
                    autoComplete='given-name'
                    className={clsxm(field, 'mt-2')}
                  />
                </div>
                <div>
                  <label htmlFor='last-name' className={label}>
                    Last name
                  </label>
                  <input
                    id='last-name'
                    name='last-name'
                    type='text'
                    required
                    autoComplete='family-name'
                    className={clsxm(field, 'mt-2')}
                  />
                </div>
                <div>
                  <label htmlFor='email' className={label}>
                    Work email
                  </label>
                  <input
                    id='email'
                    name='email'
                    type='email'
                    required
                    autoComplete='email'
                    className={clsxm(field, 'mt-2')}
                  />
                </div>
                <div>
                  <label htmlFor='phone' className={label}>
                    Phone <span className={tone.quiet}>(optional)</span>
                  </label>
                  <input
                    id='phone'
                    name='phone'
                    type='tel'
                    autoComplete='tel'
                    className={clsxm(field, 'mt-2')}
                  />
                </div>
                <div className='sm:col-span-2'>
                  <label htmlFor='company' className={label}>
                    Company <span className={tone.quiet}>(optional)</span>
                  </label>
                  <input
                    id='company'
                    name='company'
                    type='text'
                    autoComplete='organization'
                    className={clsxm(field, 'mt-2')}
                  />
                </div>
                <fieldset className='sm:col-span-2'>
                  <legend className={label}>Team size</legend>
                  <div className='mt-2 grid grid-cols-3 gap-2.5'>
                    {TEAM_SIZES.map((size, index) => (
                      <label
                        key={size.value}
                        className={clsxm(
                          'flex cursor-pointer items-center justify-center rounded-xl px-2 py-2.5 text-center text-sm font-medium ring-1 ring-inset ring-[#0f0f28]/[0.12] transition-colors hover:bg-[#f3f3f8] has-[:checked]:bg-primary-50 has-[:checked]:text-primary-700 has-[:checked]:ring-2 has-[:checked]:ring-primary-600 has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-primary-600 dark:ring-white/[0.12] dark:hover:bg-white/[0.06] dark:has-[:checked]:bg-primary-500/15 dark:has-[:checked]:text-primary-300 dark:has-[:checked]:ring-primary-400',
                          tone.body,
                        )}
                      >
                        <input
                          type='radio'
                          name='team-size'
                          value={size.value}
                          defaultChecked={index === 0}
                          className='sr-only'
                        />
                        {size.label}
                      </label>
                    ))}
                  </div>
                </fieldset>
                <div className='sm:col-span-2'>
                  <div className='flex items-baseline justify-between gap-4'>
                    <label htmlFor='message' className={label}>
                      What would you like your team to learn?
                    </label>
                    <span className={clsxm('flex-none text-xs', tone.quiet)}>
                      Max 500 characters
                    </span>
                  </div>
                  <textarea
                    id='message'
                    name='message'
                    rows={4}
                    maxLength={500}
                    className={clsxm(field, 'mt-2 resize-y')}
                    defaultValue=''
                  />
                </div>
              </div>

              <Turnstile
                ref={turnstileRef}
                action={WORKSHOP_CONTACT_TURNSTILE_ACTION}
                context='contact'
                className='mt-6'
              />

              <button
                type='submit'
                disabled={sending}
                className={clsxm(
                  buttonPrimary,
                  'mt-7 w-full disabled:cursor-wait disabled:opacity-70',
                )}
              >
                {sending ? (
                  <svg
                    className='h-4 w-4 animate-spin'
                    viewBox='0 0 24 24'
                    fill='none'
                    aria-hidden='true'
                  >
                    <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='4'
                    />
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z'
                    />
                  </svg>
                ) : null}
                {sending ? 'Sending…' : 'Send request'}
              </button>
            </form>
          </div>
        </Stage>
      </div>
    </section>
  );
}
